<template>
    <div class="course-list-container">
        <el-button type="warning" @click="filterDrawer = true" class="filter-button">{{ filterUsed && filterUsed.length > 0 ? filterUsed : $t('Course Filters')}}</el-button>
        <el-drawer v-model="filterDrawer" :title="$t('Filters')" direction="ttb" size="260">
            <div class="filters-section">
                <CategorySelector v-model="selectedCategory" :placeholder="$t('Select a category')" class="input-common" multiple />
                <SemesterSelector v-model="selectedSemester" :placeholder="$t('Select a semester')" class="input-common" />
                <el-input v-model="search" :placeholder="$t('Search')" class="input-common" @input="handleSearch" clearable />
            </div>
        </el-drawer>
        <div class="courses-section">
            <ul v-infinite-scroll="fetchNextPage" :infinite-scroll-disabled="loading" class="course-list"
                infinite-scroll-immediate="false">
                <li v-for="course in courses" :key="course.id" class="course-item" @click="goto(course.id)">
                    <el-card shadow="hover">
                        <template #header>
                            <div class="course-header">
                                <h3>{{ course.name }}
                                    <el-tag v-if="course.needLottery" type="success" class="my-tag">{{ $t('Lottery') }}</el-tag>
                                    <el-tag v-if="course.needInterview" type="warning" class="my-tag">{{ $t('Interview') }}</el-tag>
                                </h3>
                            </div>
                        </template>
                        <div class="course-content">
                            <div class="course-info">
                                <div class="info-row">
                                    <span class="info-label">{{ $t('Category') }}:</span>
                                    <span class="info-value">{{ course.category?.name }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">{{ $t('Students') }}:</span>
                                    <span class="info-value" :style="{
                                        color: (course.currentStudent || 0) > course.maxStudent ? 'red' : 
                                              (course.currentStudent || 0) === course.maxStudent ? 'orange' : 
                                              'green'
                                    }">{{ course.currentStudent || 0 }}/{{ course.maxStudent }}</span>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">{{ $t('Lessons') }}:</span>
                                    <span class="info-value">{{ course.lessonCount }}</span>
                                </div>
                                <div class="info-row" v-if="course.schedules && course.schedules.length > 0">
                                    <span class="info-label">{{ $t('Schedule') }}:</span>
                                    <div class="schedule-summary">
                                        <span class="schedule-count"
                                            :style="{ color: course.schedules.length < course.lessonCount ? 'orange' : course.schedules.length > course.lessonCount ? 'red' : '#2196F3' }">{{
                                            course.schedules.length }} schedules</span>
                                        <span class="schedule-location" style="color: #4CAF50">{{ [...new
                                            Set(course.schedules.map(s => s.room?.name))].join(', ') }}, {{ [...new
                                            Set(course.schedules.flatMap(s => (s.teachers || []).map(t =>
                                            t.fullName)))].join(', ') }}</span>
                                        <span class="schedule-time" style="color: #FF9800">{{ course.schedules.map(s =>
                                            new Date(s.startDate).toLocaleDateString('en-US', {weekday:
                                            'short'})).filter((v, i, a) => a.indexOf(v) === i).join(', ') }} {{
                                            course.schedules.map(s => `${new
                                            Date(s.startDate).toLocaleTimeString('en-US', {hour: '2-digit',
                                            minute:'2-digit'})} - ${new Date(s.endDate).toLocaleTimeString('en-US',
                                            {hour: '2-digit', minute:'2-digit'})}`).filter((v, i, a) => a.indexOf(v) ===
                                            i).join(', ') }}</span>
                                    </div>
                                </div>
                                <div class="info-row">
                                    <span class="info-label">{{ $t('Price') }}:</span>
                                    <span class="info-value">{{ course.tuition + course.insurance + course.material +
                                        course.other }}</span>
                                </div>
                            </div>
                        </div>
                    </el-card>
                </li>
                <div v-if="loading" class="loading-indicator">
                    <el-card class="loading-card">
                        <el-row justify="center" align="middle">
                            <el-col :span="24" class="text-center">
                                <div class="loading-spinner">
                                    <svg class="circular" viewBox="25 25 50 50">
                                        <circle class="path" cx="50" cy="50" r="20" fill="none" />
                                    </svg>
                                </div>
                                <div class="loading-text">{{ $t('Loading') }}...</div>
                            </el-col>
                        </el-row>
                    </el-card>
                </div>
                <div v-if="error" class="error-message">
                    {{ error }}
                </div>
            </ul>
        </div>
    </div>
</template>

<script>
import SemesterSelector from '../components/SemesterSelector.vue';
import CategorySelector from '../components/CategorySelector.vue';
import axios from 'axios';
import { BACKEND_URL } from '../../../share-source/src/config/config.js';
import { debounce } from 'lodash';
// import { CourseStatus } from '../constants/CourseStatus.js';

export default {
    name: 'CourseList',
    components: {
        SemesterSelector,
        CategorySelector
    },
    data() {
        return {
            filterDrawer: false,
            courses: [],
            selectedSemester: null,
            selectedCategory: null,
            categories: [],
            search: '',
            loading: false,
            error: null,
            currentPage: 0,
            hasMore: true,
            pageSize: 20
        }
    },
    created() {
        this.debouncedSearch = debounce(this.fetchCourses, 300);
    },
    mounted() {
        // this.fetchCategories();
        this.fetchCourses();
    },
    computed: {
        groupedCategories() {
            const itemsPerPage = 8; // 4x2 grid
            const groups = [];
            for (let i = 0; i < this.categories.length; i += itemsPerPage) {
                groups.push(this.categories.slice(i, i + itemsPerPage));
            }
            return groups;
        },
        filterUsed() {
            let result = '';
            if (this.selectedCategory && this.selectedCategory.length > 0) {
                // console.log(this.selectedCategory);
                // console.log(this.categories);
                result += this.selectedCategory.length + ' Categories; ';
            }
            if (this.selectedSemester) {
                result += 'Semester; ';
            }
            if (this.search) {
                result += this.search;
            }
            return result;
        }
    },
    methods: {
        // fetchCategories() {
        //     axios.get(`${BACKEND_URL}/api/category/all/nonleaf`).then(response => {
        //         this.categories = response.data;
        //     });
        // },
        handleSearch() {
            this.currentPage = 0;
            this.courses = [];
            this.hasMore = true;
            this.debouncedSearch();
        },
        async fetchNextPage() {
            if (!this.hasMore || this.loading) return;
            
            this.currentPage += 1;
            await this.fetchCourses();
        },
        async fetchCourses() {
            try {
                this.loading = true;
                this.error = null;
                
                const params = new URLSearchParams();
                params.append('published', true);
                if (this.selectedSemester) params.append('semesterId', this.selectedSemester);
                if (this.selectedCategory) {
                    if (Array.isArray(this.selectedCategory)) {
                        this.selectedCategory.forEach(id => params.append('categoryId', id));
                    } else {
                        params.append('categoryId', this.selectedCategory);
                    }
                }
                if (this.search) params.append('name', this.search);
                params.append('page', this.currentPage);
                params.append('size', this.pageSize);

                const response = await axios.get(`${BACKEND_URL}/api/course/search/paged`, {
                    params,
                    withCredentials: true
                });

                if (this.currentPage === 0) {
                    this.courses = response.data.content;
                } else {
                    this.courses = [...this.courses, ...response.data.content];
                }
                
                this.hasMore = !response.data.last;
            } catch (err) {
                this.error = 'Failed to fetch courses. Please try again.';
                console.error('Error fetching courses:', err);
            } finally {
                this.loading = false;
            }
        },
        goto(courseId) {
            this.$router.push({ 
                name: 'SignupViewByCourse', 
                params: { courseId: courseId } 
            });
        },
    },
    watch: {
        selectedCategory() {
            this.currentPage = 0;
            this.courses = [];
            this.hasMore = true;
            this.fetchCourses();
        },
        selectedSemester() {
            this.currentPage = 0;
            this.courses = [];
            this.hasMore = true;
            this.fetchCourses();
        }
    }
}

</script>

<style scoped>
.my-tag {
    margin-left: 10px;
    float: right;
}   

.course-list-container {
    display: flex;
    flex-direction: column;
    /* margin: 8px; */
    /* max-height: 100vh; */
    /* padding: 20px; */
}

.filters-section {
    flex: 0 0 auto;
    margin-bottom: 4px;
}

.courses-section {
    flex: 1;
    overflow: hidden;
    position: relative;
}

.course-list {
    height: 100%;
    overflow-y: auto;
    padding: 0;
    margin: 0;
    list-style: none;
}

.course-item {
    margin-bottom: 16px;
    list-style-type: none;
}

.course-content {
    padding: 8px;
}

.course-title {
    margin: 0 0 8px 0;
    font-size: 16px;
    font-weight: 600;
    color: #303133;
}

.course-description {
    margin: 0;
    font-size: 14px;
    color: #606266;
    line-height: 1.4;
}

.loading-indicator {
    text-align: center;
    padding: 20px 0;
}

.loading-card {
    background: transparent;
    border: none;
    box-shadow: none;
    padding: 15px;
}

.loading-text {
    color: #909399;
    font-size: 14px;
    margin-top: 8px;
}

.text-center {
    text-align: center;
}

/* Custom spinner styles */
.loading-spinner {
    position: relative;
    display: inline-block;
    width: 24px;
    height: 24px;
}

.circular {
    animation: loading-rotate 2s linear infinite;
    height: 100%;
    width: 100%;
    transform-origin: center center;
}

.path {
    stroke-dasharray: 90, 150;
    stroke-dashoffset: 0;
    stroke-width: 2;
    stroke: #409EFF;
    stroke-linecap: round;
    animation: loading-dash 1.5s ease-in-out infinite;
}

@keyframes loading-rotate {
    100% {
        transform: rotate(360deg);
    }
}

@keyframes loading-dash {
    0% {
        stroke-dasharray: 1, 200;
        stroke-dashoffset: 0;
    }
    50% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -40px;
    }
    100% {
        stroke-dasharray: 90, 150;
        stroke-dashoffset: -120px;
    }
}

.error-message {
    text-align: center;
    color: #f56c6c;
    padding: 20px 0;
}

.input-common {
    margin-bottom: 15px;
    width: 100%;
}

/* Ensure the scrollbar doesn't cause horizontal overflow */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
}

::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #555;
}

/* Optional: Customize loading appearance */
:deep(.el-loading-mask) {
    z-index: 1000;
}

.course-info {
    margin-top: 8px;
}

.info-row {
    margin-bottom: 4px;
    display: flex;
    align-items: flex-start;
}

.info-label {
    font-weight: 600;
    color: #606266;
    min-width: 80px;
    text-align: right;
    margin-right: 10px;
}

.info-value {
    text-align: left;
    /* display: flex; */
}

.schedule-summary {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 13px;
}

.schedule-count {
    color: #2196F3;
}

.schedule-location {
    color: #4CAF50;
}

.schedule-time {
    color: #FF9800;
}

:deep(.el-card) {
    --el-card-padding: 4px;
}

h3 {
    margin: 4px;
}

.filter-button {
    margin: 4px;
}

</style>
